/**
 * Payment Service - Handles all payment-related API calls
 * 
 * This service communicates with your backend API for PhonePe integration.
 * Update the API_BASE_URL with your actual backend URL.
 * 
 * IMPORTANT: PhonePe expects amounts in PAISE (1 INR = 100 paise)
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://willovate.in/payment/api';

/**
 * Utility function to convert INR to paise
 * PhonePe requires amounts in paise (smallest currency unit)
 * @param {number} amountInINR - Amount in Indian Rupees
 * @returns {number} - Amount in paise
 */
const convertToPaise = (amountInINR) => {
  return Math.round(amountInINR * 100);
};

/**
 * Utility function to convert paise to INR
 * @param {number} amountInPaise - Amount in paise
 * @returns {number} - Amount in Indian Rupees
 */
const convertToINR = (amountInPaise) => {
  return (amountInPaise / 100).toFixed(2);
};

/**
 * Format amount in INR with currency symbol
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted amount
 */
const formatINR = (amount) => {
  return `₹${parseFloat(amount).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const PaymentService = {
  /**
   * Create a new payment order with PhonePe
   * @param {Object} paymentData - Payment details
   * @param {number} paymentData.amount - Amount in INR (will be converted to paise)
   * @param {string} paymentData.orderId - Unique order ID from the originating app
   * @param {string} paymentData.description - Payment description
   * @param {string} paymentData.customerName - Customer name
   * @param {string} paymentData.customerPhone - Customer phone number
   * @param {string} paymentData.customerEmail - Customer email
   * @param {string} paymentData.appName - Name of the originating application
   * @returns {Promise<Object>} - Payment order response with redirect URL
   */
  createPaymentOrder: async (paymentData) => {
    try {
      // Convert amount to paise for PhonePe
      const amountInPaise = convertToPaise(paymentData.amount);
      
      console.log(`Creating payment order: ₹${paymentData.amount} INR = ${amountInPaise} paise`);
      
      const response = await fetch(`${API_BASE_URL}/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amountInPaise, // Send amount in paise to backend
          amountInINR: paymentData.amount, // Also send original INR amount for reference
          orderId: paymentData.orderId,
          description: paymentData.description,
          customerName: paymentData.customerName,
          customerPhone: paymentData.customerPhone,
          customerEmail: paymentData.customerEmail,
          appName: paymentData.appName,
          currency: 'INR',
          redirectUrl: `${window.location.origin}/payment/callback`,
          callbackUrl: `${API_BASE_URL}/payment/webhook`
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create payment order');
      }

      return {
        success: true,
        data: {
          transactionId: data.transactionId,
          merchantTransactionId: data.merchantTransactionId,
          redirectUrl: data.redirectUrl,
          orderId: paymentData.orderId,
          amount: paymentData.amount,
          amountInPaise: amountInPaise
        }
      };
    } catch (error) {
      console.error('Create payment order error:', error);
      return {
        success: false,
        message: error.message || 'Failed to create payment order'
      };
    }
  },

  /**
   * Verify payment status after callback from PhonePe
   * @param {Object} params - Verification parameters
   * @param {string} params.transactionId - Transaction ID from PhonePe
   * @param {string} params.code - Status code from PhonePe callback
   * @returns {Promise<Object>} - Payment verification result
   */
  verifyPayment: async ({ transactionId, code }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transactionId,
          code
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Payment verification failed');
      }

      return {
        success: true,
        data: {
          status: data.status, // 'success', 'failed', 'pending'
          transactionId: data.transactionId,
          orderId: data.orderId,
          amount: data.amount,
          paymentMethod: data.paymentMethod,
          timestamp: data.timestamp,
          callbackUrl: data.callbackUrl,
          appName: data.appName
        }
      };
    } catch (error) {
      console.error('Verify payment error:', error);
      return {
        success: false,
        message: error.message || 'Payment verification failed'
      };
    }
  },

  /**
   * Check payment status by order ID
   * @param {string} orderId - Order ID to check
   * @returns {Promise<Object>} - Payment status
   */
  checkPaymentStatus: async (orderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/status/${orderId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to check payment status');
      }

      return {
        success: true,
        data: {
          status: data.status,
          transactionId: data.transactionId,
          orderId: data.orderId,
          amount: data.amount,
          timestamp: data.timestamp
        }
      };
    } catch (error) {
      console.error('Check payment status error:', error);
      return {
        success: false,
        message: error.message || 'Failed to check payment status'
      };
    }
  },

  /**
   * Get payment history for an order
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} - Payment history
   */
  getPaymentHistory: async (orderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/history/${orderId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to get payment history');
      }

      return {
        success: true,
        data: data.payments
      };
    } catch (error) {
      console.error('Get payment history error:', error);
      return {
        success: false,
        message: error.message || 'Failed to get payment history'
      };
    }
  },

  // Utility functions exposed for use in components
  convertToPaise,
  convertToINR,
  formatINR
};

export default PaymentService;
